import { ref } from 'vue'
import { marked } from 'marked'
import DOMPurify from 'dompurify'

const cache = new Map()

// Configure marked options for better security and rendering
marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
})

export function useMarkdownLoader() {
  const content = ref('')
  const isLoading = ref(false)
  const error = ref(null)

  const loadMarkdown = async (url) => {
    if (!url) {
      error.value = 'No URL provided'
      return
    }

    // Check cache first
    if (cache.has(url)) {
      content.value = cache.get(url)
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`Failed to fetch markdown: ${response.status} ${response.statusText}`)
      }

      const markdownText = await response.text()

      // Parse markdown to HTML
      const rawHtml = await marked.parse(markdownText)

      // Sanitize HTML to prevent XSS attacks
      // Allow common HTML elements needed for markdown rendering
      const sanitizedHtml = DOMPurify.sanitize(rawHtml, {
        ALLOWED_TAGS: [
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'p', 'br', 'hr',
          'strong', 'em', 'b', 'i', 'u', 's', 'del', 'mark',
          'ul', 'ol', 'li',
          'a',
          'blockquote', 'pre', 'code',
          'table', 'thead', 'tbody', 'tr', 'th', 'td',
          'div', 'span',
        ],
        ALLOWED_ATTR: [
          'href', 'title', 'target', 'rel',
          'class', 'id',
          'colspan', 'rowspan',
        ],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      })

      // Cache the sanitized result
      cache.set(url, sanitizedHtml)
      content.value = sanitizedHtml
    } catch (err) {
      error.value = err.message
      console.error('Markdown loading error:', err)
    } finally {
      isLoading.value = false
    }
  }

  const clearCache = () => {
    cache.clear()
  }

  return {
    content,
    isLoading,
    error,
    loadMarkdown,
    clearCache,
  }
}
